'use client';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { useState, useEffect, useRef } from 'react';
import Button from '@mui/material/Button';
import dynamic from 'next/dynamic';
import useMediaQuery from '@mui/material/useMediaQuery';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import useWindowSize from '@repo/ui/src/components/shared/window-size';
import buildLinkObj, { getRel } from '@repo/ui/src/functions/build-link';
import { imageFullWidthXs, imageFullWidthSmAndUp } from '@style/variables';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Root, StyledContainer } from './styles';
import BrandDivider from '@components/BrandDivider';

const LifestyleBanner = ({
  backgroundImage,
  title,
  subtitle,
  description,
  link,
  alignment,
  verticalAlignment,
  cardStyle,
  showDivider,
}) => {
  // Allow users to specify linebreak with \n in Amplience
  const splitTitle = title.split('\\n').map((x, i) => <div key={`${x}_${i}`}>{x}</div>);
  const [isServer, setIsServer] = useState(true);
  const VideoRendered = dynamic(import('@repo/ui/src/components/BackgroundVideo'), {
    ssr: isServer,
  });
  useEffect(() => setIsServer(false), []);

  const theme = useTheme();
  const [width] = useWindowSize();
  const smAndDown = useMediaQuery(theme.breakpoints.down('md'));
  const xsOnly = useMediaQuery(theme.breakpoints.only('xs'));
  const linkObj = buildLinkObj(link);
  const alignRight = !xsOnly && alignment === 'right';
  const animateFromDirection =
    cardStyle && (xsOnly || !alignment || alignment === 'left')
      ? 'left'
      : cardStyle && alignment === 'right'
        ? 'right'
        : 'bottom';

  const IMAGE_META_SCHEMA = 'http://bigcontent.io/cms/schema/v1/core#/definitions/image-link';
  const VIDEO_META_SCHEMA = 'http://bigcontent.io/cms/schema/v1/core#/definitions/video-link';
  const FULL_TO_THEATER = 1920;

  let imgUrl;
  let isVideo;
  let vidWidth;

  if (backgroundImage && backgroundImage._meta.schema === IMAGE_META_SCHEMA) {
    isVideo = false;
    imgUrl = buildImageSrc(
      backgroundImage,
      xsOnly ? imageFullWidthXs : imageFullWidthSmAndUp,
      xsOnly ? 3 : 4,
      xsOnly ? 5 : 3,
    );
  } else if (backgroundImage && backgroundImage._meta.schema === VIDEO_META_SCHEMA) {
    isVideo = true;
    vidWidth = width > FULL_TO_THEATER ? FULL_TO_THEATER : width;
    if (!vidWidth) {
      vidWidth = xsOnly ? imageFullWidthXs : imageFullWidthSmAndUp;
    }
  }

  const [inView, setInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    ScrollTrigger.create({
      trigger: ref.current,
      start: 'top 75%',
      onEnter: () => setInView(true),
    });
  }, []);

  return (
    <Root
      imgUrl={imgUrl}
      ref={ref}
      className={`lifestyle-banner ${imgUrl ? 'image-background' : 'video-background'} ${
        cardStyle ? 'card-style' : ''
      }`}
    >
      {isVideo && <VideoRendered video={backgroundImage} width={vidWidth} />}
      <StyledContainer
        verticalAlignment={verticalAlignment}
        cardStyle={cardStyle}
        alignRight={alignRight}
        className={`${cardStyle ? '' : 'black-overlay'} animate-${animateFromDirection} ${
          inView ? 'in-view' : ''
        }`}
      >
        <Grid
          container
          display="flex"
          flexDirection="column"
          alignItems={alignRight ? 'flex-end' : 'flex-start'}
        >
          <Grid item xs={12} sm={6} md={5} lg={4} xl={3}>
            <div className="card-content animate">
              {title && (
                <Typography
                  component="h1"
                  variant="h2"
                  color={(theme) => (backgroundImage ? 'white' : theme.palette.neutral[800])}
                  className={cardStyle ? '' : 'animate'}
                  fontSize={{ xs: '2rem', sm: '3rem' }}
                  lineHeight={{ xs: '1.8rem', sm: '2.6rem' }}
                >
                  {splitTitle}
                  {subtitle && <span className="subtitle">{subtitle}</span>}
                </Typography>
              )}
              {showDivider && (
                <BrandDivider align="left" sx={{ mb: { xs: 2, sm: 3 } }} className="animate" />
              )}
              {description && (
                <Typography
                  variant="body2"
                  mt={showDivider ? 1 : 3}
                  color={(theme) => (backgroundImage ? 'white' : theme.palette.neutral[800])}
                  className={cardStyle ? '' : 'animate'}
                >
                  {description}
                </Typography>
              )}
              {linkObj?.text && (
                <Box
                  mt={{ xs: 3, sm: 4 }}
                  className={`${cardStyle ? '' : 'animate'} button-wrapper`}
                >
                  <Button
                    rel={getRel(linkObj)}
                    href={linkObj.href}
                    variant="outlined"
                    size={smAndDown ? 'small' : 'medium'}
                    color="inherit"
                    fullWidth={xsOnly}
                  >
                    {linkObj.text}
                  </Button>
                </Box>
              )}
            </div>
          </Grid>
        </Grid>
      </StyledContainer>
    </Root>
  );
};

export default LifestyleBanner;
